import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * bn-card is the standard bordered content surface. An optional `heading`
 * renders a titled header row with an `actions` slot (buttons on the right); the
 * default slot is the body. Use it for list containers, forms, and detail
 * sections.
 *
 * Properties:
 * - `heading` — header title; when empty no header row is rendered
 * - `flush`   — removes body padding, e.g. when the body is a full-bleed
 *               bn-table; reflected, so `bn-card[flush]` is selectable
 *
 * Slots: the default slot is the body; `actions` sits at the right of the
 * header row.
 *
 * Events: none.
 *
 * The heading is presentational — a card does not impose a level on the
 * page's document outline. A page that needs a real heading puts one in the
 * body or labels the section itself.
 *
 *   <bn-card heading="Datasources">
 *     <bn-button slot="actions" variant="primary">Add datasource</bn-button>
 *     …body…
 *   </bn-card>
 */
@customElement("bn-card")
export class BnCard extends LitElement {
  @property() heading = "";
  /** Removes body padding (e.g. when the body is a full-bleed bn-table). */
  @property({ type: Boolean, reflect: true }) flush = false;

  static override styles = css`
    :host {
      display: block;
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius-lg);
      background: var(--bino-surface);
      box-shadow: var(--bino-shadow-header);
      overflow: hidden;
    }
    .head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-md);
      padding: var(--bino-space-md) var(--bino-space-lg);
      border-bottom: 1px solid var(--bino-border);
    }
    .title {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
      color: var(--bino-text);
    }
    .spacer {
      flex: 1;
    }
    .body {
      padding: var(--bino-space-lg);
    }
    .body.flush {
      padding: 0;
    }
  `;

  override render() {
    return html`
      ${this.heading
        ? html`<div class="head">
            <span class="title">${this.heading}</span>
            <span class="spacer"></span>
            <slot name="actions"></slot>
          </div>`
        : ""}
      <div class="body ${this.flush ? "flush" : ""}"><slot></slot></div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-card": BnCard;
  }
}
